import type { CoverageCaseKind } from "./types";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import { shouldAutoSeedDemoData } from "@/core/demo/is-demo-mode-enabled";

const RULES_KEY = "hospitol.insurerValidationRules";
const RULES_SEED_KEY = "hospitol.insurerValidationRules.seeded";

/** Règles de validation assureur — configurables par compagnie / entreprise. */
export interface InsurerValidationRules {
  insurerId: string;
  /** Active le contrôle pour cet assureur (sinon aucune soumission auto). */
  enabled: boolean;
  /** Montant estimé au-delà duquel une validation est requise (FC). */
  amountThreshold: number;
  /** Mots-clés actes esthétiques / cosmétiques (motif, actes, diagnostic, médicaments). */
  aestheticKeywords: string[];
  /** Actes ou traitements nécessitant un accord préalable explicite. */
  priorAuthKeywords: string[];
  /** Médicaments (libellé ou code) exigeant validation assureur sur ordonnance. */
  priorAuthMedications: string[];
  /** Analyses labo bloquées sans accord préalable (ex. fertilité). */
  blockedLabTests: string[];
  /** Examens imagerie bloqués sans accord préalable. */
  blockedImagingTypes: string[];
  /** Soumettre les ordonnances pharmacie si les règles ci-dessus matchent. */
  validatePrescriptions: boolean;
  /** Soumettre consultations / actes cliniques si règles matchent. */
  validateConsultations: boolean;
  /** Contrôler les demandes laboratoire (liste bloquée). */
  validateLaboratory: boolean;
  /** Contrôler les demandes imagerie (liste bloquée). */
  validateImaging: boolean;
}

export interface InsurerValidationContext {
  kind: CoverageCaseKind;
  motif?: string;
  diagnostic?: string;
  acts?: string;
  examLabel?: string;
  prescriptionLines?: Array<{ label: string; quantity: number }>;
  estimatedAmount?: number;
}

export interface InsurerValidationResult {
  required: boolean;
  reasons: string[];
}

const DEFAULT_AESTHETIC_KEYWORDS = [
  "esthétique",
  "esthetique",
  "cosmétique",
  "cosmetique",
  "botox",
  "blanchiment",
  "liposuccion",
  "lifting",
];

const DEFAULT_PRIOR_AUTH_MEDICATIONS = ["infliximab", "sku-med-010", "med-010"];

const DEFAULT_BLOCKED_LAB_TESTS = [
  "fertilité",
  "fertilite",
  "fsh",
  "lh",
  "amh",
  "spermogramme",
  "ovarien",
  "réserve ovarienne",
];

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = getStorageItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeRules(list: InsurerValidationRules[]): void {
  setStorageItem(RULES_KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent("hospitol:insurer-rules"));
}

function defaultRules(insurerId: string): InsurerValidationRules {
  return {
    insurerId,
    enabled: true,
    amountThreshold: 75_000,
    aestheticKeywords: [...DEFAULT_AESTHETIC_KEYWORDS],
    priorAuthKeywords: ["implant", "prothèse", "chimiothérapie", "irm"],
    priorAuthMedications: [...DEFAULT_PRIOR_AUTH_MEDICATIONS],
    blockedLabTests: [...DEFAULT_BLOCKED_LAB_TESTS],
    blockedImagingTypes: [],
    validatePrescriptions: true,
    validateConsultations: true,
    validateLaboratory: true,
    validateImaging: false,
  };
}

function normalizeRules(raw: Record<string, unknown>): InsurerValidationRules {
  const defaults = defaultRules(String(raw.insurerId ?? ""));
  return {
    insurerId: String(raw.insurerId ?? ""),
    enabled: raw.enabled !== false,
    amountThreshold: Number(raw.amountThreshold ?? defaults.amountThreshold),
    aestheticKeywords: Array.isArray(raw.aestheticKeywords)
      ? (raw.aestheticKeywords as string[]).map((k) => k.trim().toLowerCase()).filter(Boolean)
      : defaults.aestheticKeywords,
    priorAuthKeywords: Array.isArray(raw.priorAuthKeywords)
      ? (raw.priorAuthKeywords as string[]).map((k) => k.trim().toLowerCase()).filter(Boolean)
      : defaults.priorAuthKeywords,
    priorAuthMedications: Array.isArray(raw.priorAuthMedications)
      ? (raw.priorAuthMedications as string[]).map((k) => k.trim().toLowerCase()).filter(Boolean)
      : defaults.priorAuthMedications,
    blockedLabTests: Array.isArray(raw.blockedLabTests)
      ? (raw.blockedLabTests as string[]).map((k) => k.trim().toLowerCase()).filter(Boolean)
      : defaults.blockedLabTests,
    blockedImagingTypes: Array.isArray(raw.blockedImagingTypes)
      ? (raw.blockedImagingTypes as string[]).map((k) => k.trim().toLowerCase()).filter(Boolean)
      : defaults.blockedImagingTypes,
    validatePrescriptions: raw.validatePrescriptions !== false,
    validateConsultations: raw.validateConsultations !== false,
    validateLaboratory: raw.validateLaboratory !== false,
    validateImaging: raw.validateImaging === true,
  };
}

export function ensureDemoInsurerRules(insurerIds: string[]): void {
  if (!shouldAutoSeedDemoData()) return;
  const list = readJson<InsurerValidationRules[]>(RULES_KEY, []);
  let changed = false;
  for (const insurerId of insurerIds) {
    const index = list.findIndex((r) => r.insurerId === insurerId);
    if (index < 0) {
      list.push(defaultRules(insurerId));
      changed = true;
    } else {
      const normalized = normalizeRules(list[index] as unknown as Record<string, unknown>);
      if (!Array.isArray((list[index] as InsurerValidationRules).priorAuthMedications)) {
        list[index] = normalized;
        changed = true;
      }
    }
  }
  if (changed) writeRules(list);
  if (!getStorageItem(RULES_SEED_KEY) && insurerIds.length > 0) {
    setStorageItem(RULES_SEED_KEY, new Date().toISOString());
  }
}

export function getInsurerValidationRules(insurerId: string): InsurerValidationRules {
  const list = readJson<Record<string, unknown>[]>(RULES_KEY, []).map(normalizeRules);
  return list.find((r) => r.insurerId === insurerId) ?? defaultRules(insurerId);
}

export function saveInsurerValidationRules(rules: InsurerValidationRules): void {
  const list = readJson<Record<string, unknown>[]>(RULES_KEY, []).map(normalizeRules);
  const index = list.findIndex((r) => r.insurerId === rules.insurerId);
  const normalized = normalizeRules(rules as unknown as Record<string, unknown>);
  if (index >= 0) list[index] = normalized;
  else list.push(normalized);
  writeRules(list);
}

function kindEnabled(rules: InsurerValidationRules, kind: CoverageCaseKind): boolean {
  if (kind === "prescription") return rules.validatePrescriptions;
  if (kind === "consultation") return rules.validateConsultations;
  if (kind === "laboratory") return rules.validateLaboratory;
  if (kind === "imaging") return rules.validateImaging;
  if (kind === "hospitalization") return true;
  return true;
}

function textBlob(ctx: InsurerValidationContext): string {
  const parts = [
    ctx.motif,
    ctx.diagnostic,
    ctx.acts,
    ctx.examLabel,
    ...(ctx.prescriptionLines?.map((l) => l.label) ?? []),
  ];
  return parts.join(" ").toLowerCase();
}

function matchesKeywords(text: string, keywords: string[]): string[] {
  return keywords.filter((kw) => kw && text.includes(kw));
}

function matchesPriorAuthMedications(
  lines: InsurerValidationContext["prescriptionLines"],
  medications: string[],
): string[] {
  if (!lines?.length || !medications.length) return [];
  const blob = lines.map((l) => l.label.toLowerCase()).join(" ");
  return medications.filter((med) => med && blob.includes(med));
}

/** Détermine si un acte doit être soumis à validation assureur selon les règles du contrat. */
export function evaluateInsurerValidationNeed(
  insurerId: string,
  ctx: InsurerValidationContext,
): InsurerValidationResult {
  const rules = getInsurerValidationRules(insurerId);
  if (!rules.enabled || !kindEnabled(rules, ctx.kind)) {
    return { required: false, reasons: [] };
  }

  const reasons: string[] = [];
  const text = textBlob(ctx);

  if (ctx.kind === "laboratory") {
    const blocked = matchesKeywords(text, rules.blockedLabTests);
    if (blocked.length > 0) {
      reasons.push(`Analyse soumise à accord préalable (${blocked[0]})`);
    }
    return { required: reasons.length > 0, reasons };
  }

  if (ctx.kind === "imaging") {
    const blocked = matchesKeywords(text, rules.blockedImagingTypes);
    if (blocked.length > 0) {
      reasons.push(`Examen soumis à accord préalable (${blocked[0]})`);
    }
    return { required: reasons.length > 0, reasons };
  }

  const medHits = matchesPriorAuthMedications(ctx.prescriptionLines, rules.priorAuthMedications);
  if (medHits.length > 0) {
    reasons.push(`Médicament soumis à validation assureur (${medHits[0]})`);
  }

  const amount = ctx.estimatedAmount ?? 0;
  if (amount > 0 && amount >= rules.amountThreshold) {
    reasons.push(`Montant estimé ≥ ${rules.amountThreshold.toLocaleString("fr-FR")} FC`);
  }

  const aesthetic = matchesKeywords(text, rules.aestheticKeywords);
  if (aesthetic.length > 0) {
    reasons.push(`Acte esthétique / cosmétique (${aesthetic[0]})`);
  }

  const prior = matchesKeywords(text, rules.priorAuthKeywords);
  if (prior.length > 0) {
    reasons.push(`Accord préalable requis (${prior[0]})`);
  }

  return { required: reasons.length > 0, reasons };
}
